import React from 'react';
import { Link } from 'react-router-dom';
import { getCurrentSeason, getSeasonTitle, getMainSeasons } from '../utils/currentSeason';
import '../styles/PosterCard.css';

// Status combined with current progress (e.g. "Watching: S3, Ep 5/12"), always shown so the
// card never looks "empty" - mirrors the per-type logic in MediaItem/MediaTags.js.
const getStatusLine = (media) => {
  const status = media.status || 'Unknown';
  switch (media.type) {
    case 'SteamGame': {
      const hours = media.playtime ? `${(media.playtime / 60).toFixed(1)}h played` : null;
      return hours ? `${status} · ${hours}` : status;
    }
    case 'Anime':
    case 'AnimeMovie':
    case 'AnimeSerie': {
      const current = getCurrentSeason(media);
      const currentStatus = current.status || status;
      const hasMultipleSeasons = getMainSeasons(media).length > 1;
      if (currentStatus === 'Finished') return 'Finished';
      if (currentStatus === 'Plan to Watch') {
        return hasMultipleSeasons ? `Plan to Watch: ${getSeasonTitle(current)}` : 'Plan to Watch';
      }
      const ep = `Ep ${current.current_episode || 0}${current.episode_count ? `/${current.episode_count}` : ''}`;
      return hasMultipleSeasons ? `${currentStatus}: ${getSeasonTitle(current)}, ${ep}` : `${currentStatus}: ${ep}`;
    }
    case 'ScreenMedia': {
      const isTV = media.media_format?.toLowerCase() === 'tv';
      const current = getCurrentSeason(media);
      const currentStatus = current.status || status;
      const hasMultipleSeasons = getMainSeasons(media).length > 1;
      if (!isTV || currentStatus === 'Finished' || currentStatus === 'Plan to Watch') return currentStatus;
      const ep = `Ep ${current.current_episode ?? 0}${current.episode_count ? `/${current.episode_count}` : ''}`;
      return hasMultipleSeasons ? `${currentStatus}: ${getSeasonTitle(current)}, ${ep}` : `${currentStatus}: ${ep}`;
    }
    case 'Manga': {
      if (status === 'Finished' || status === 'Plan to Read') return status;
      const vol = media.current_volume !== null && media.current_volume !== undefined ? `vol.${media.current_volume}, ` : '';
      return `${status}: ${vol}Ch.${media.current_chapter || 0}`;
    }
    case 'Book': {
      if (status === 'Finished' || status === 'Plan to Read') return status;
      return `${status}: p.${media.current_page || 0}${media.page_count ? `/${media.page_count}` : ''}`;
    }
    default:
      return status;
  }
};

const PosterCard = ({ media }) => {
  const image = media.vertical_image || media.horizontal_image || '/static/placeholder.jpg';
  // const link = `/media-list/${media.type}#search=${encodeURIComponent(media.title)}`;
  const link = `/media-item/${media.id}`;

  // Map media types to icon paths (matching ExploreButtons)
  const mediaIcons = {
    SteamGame: '/static/icons/steam.svg',
    PhysicalGame: '/static/icons/gamepad-solid.svg',
    PSP: '/static/icons/gamepad-solid.svg',
    VITA: '/static/icons/playstation.svg',
    NDS: '/static/icons/nintendo-ds.svg',
    SWITCH: '/static/icons/nintendoswitch.svg',
    vinyl: '/static/icons/record-vinyl-solid.svg',
    cd: '/static/icons/compact-disc-solid.svg',
    Anime: '/static/icons/clapperboard-solid.svg',
    Manga: '/static/icons/book-solid.svg',
    Book: '/static/icons/book-solid.svg',
    Music: '/static/icons/music-solid.svg',
    ScreenMedia: '/static/icons/film-solid.svg',
    movie: '/static/icons/film-solid.svg',
    tv: '/static/icons/tv-solid.svg',
  };

  // Determine which key to use for the icon
	let iconKey = media.type;
	if(media.type === 'PhysicalGame') {
		iconKey = media.platform.toUpperCase();
	}
	if(media.type === 'Music') {
		iconKey = media.format;
	}
  if(media.type === 'ScreenMedia') {
    iconKey = media.media_format;
	}

	// Get the icon source
	const iconSrc = mediaIcons[iconKey] || '/static/icons/file-solid.svg';

  return (
    <Link to={link} className="media-poster-card">
      <div className="poster-img-wrapper">
        <img src={image} className="poster-img" alt={media.title} />
      </div>
      <div className="poster-info">
        <strong> 
          <img src={iconSrc} class="icon media-type-icon" alt={`${media.type} icon`} /> {media.title}
        </strong>
        <p className="poster-status-line">{getStatusLine(media)}</p>
      </div>
    </Link>
  );
};

export default PosterCard;